import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    Divider,
    FormControl,
    FormControlLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { useSnackbar } from "src/utils/useSnackbar";
import {
    useCreateIngredientReference,
    IngredientReferenceWrite,
    IngredientReferenceScoreWrite,
    IngredientReferenceCompanyWrite,
} from "src/api/client-api/ingredientReference/useCreateIngredientReference";
import { IngredientReference } from "src/api/client-api/ingredientReference/useAllIngredientReferences";
import { useGetAllCampaigns, Campaign } from "src/api/client-api/useCampaign";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { AssociatedStructuresField } from "src/pages/Ingredient/IngredientForm/Pages/Page1/Fields/AssociatedStructuresField";
import type {
    IngredientFormAssociatedCompany,
    IngredientFormCompany,
} from "src/pages/Ingredient/IngredientForm/IngredientForm.types";

import { useIngredientScoreFields } from "./useIngredientScoreFields";
import { FormProductSelector } from "src/components/Selector/ProductSelector/FormProductSelector";
import { FormCpcSelector } from "src/components/Selector/CpcSelector/FormCpcSelector";
import { FormGeographySelector } from "src/components/Selector/GeographySelector/FormGeographySelector";
import { useIngredientReferenceFromProductPreview } from "src/api/client-api/ingredientReference/useIngredientReferenceFromProductPreview";

type SourceProductEntityType = "product" | "intermediate_product";

const FormSchema = z.object({
    id: z.number().optional(),
    name: z.string().min(1, "Name is required"),
    code: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    cpcCode: z.string().nullable().optional(),
    defaultOrigin: z.string().nullable().optional(),
    organic: z.boolean().nullable().optional(),
    fresh: z.boolean().nullable().optional(),
    animal: z.boolean().nullable().optional(),
    /**
     * 2D map keyed by `${scoreId}` then `${campaignId}` so we can support the
     * (scoreId, campaign) uniqueness model on the backend. Both layers use
     * stringified ids because react-hook-form keys must be strings.
     */
    scores: z.record(z.string(), z.record(z.string(), z.number().nullable())),
    /**
     * Explicit visibility list. Stored shape mirrors `IngredientFormAssociatedCompany`
     * (one row per assignment) so the existing `AssociatedStructuresField` component
     * can be reused verbatim. The `@id` is preserved for already-persisted rows so
     * API Platform's collection sync can match them by IRI; new rows are sent
     * without `@id` and the backend resolves the `Organization` from the `organization` IRI.
     *
     * Required (not `.default([])`) because Zod defaults break the input/output type
     * symmetry that `react-hook-form`'s `Resolver<FormData>` relies on; we always
     * seed `[]` in `defaultValues` and the `useEffect` reset, so the empty case is
     * covered by call-sites rather than by the schema.
     */
    ingredientReferenceCompanies: z.array(
        z.object({
            "@id": z.string().optional(),
            id: z.number().optional(),
            organization: z.object({
                "@id": z.string(),
                id: z.number(),
                name: z.string(),
            }),
        }),
    ),
});

type FormData = z.infer<typeof FormSchema>;

const campaignIdFromIri = (iri: string): number | null => {
    const segments = iri.split("/");
    const last = segments[segments.length - 1];
    const parsed = Number(last);
    return Number.isFinite(parsed) ? parsed : null;
};

const buildCampaignIri = (campaignId: number | string): IRI => `/api/campaigns/${campaignId}`;

const sortCampaignsForGrid = (campaigns: Campaign[]): Campaign[] =>
    [...campaigns].sort((a, b) => {
        const startA = Date.parse(a.startDate);
        const startB = Date.parse(b.startDate);
        if (Number.isFinite(startA) && Number.isFinite(startB)) {
            return startA - startB;
        }
        return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
    });

type Props = {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    editing?: IngredientReference | null;
};

export const IngredientReferenceModal: React.FC<Props> = ({ open, onClose, onSuccess, editing }) => {
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const { fields: scoreFields, isLoading: isLoadingFields } = useIngredientScoreFields();
    const { campaigns, isLoading: isLoadingCampaigns } = useGetAllCampaigns({ orderByDate: true });
    const { currentCampaign, selectedCompany, userCompanies, isAdmin } = useFoodpilotContext();
    const [sourceProductEntityType, setSourceProductEntityType] = useState<SourceProductEntityType | "">("");
    const [sourceProductId, setSourceProductId] = useState("");
    const appliedPreviewKeyRef = useRef<string | null>(null);

    const createMutation = useCreateIngredientReference();
    const isProcessing = createMutation.isLoading;
    const isEditing = !!editing;
    const ownerInUserScope =
        editing?.ownedBy?.id !== undefined && (userCompanies ?? []).some((c) => c.id === editing.ownedBy?.id);
    const canManageAssociations = !isEditing || isAdmin() || editing?.ownedBy == null || ownerInUserScope;

    const sortedCampaigns = useMemo(() => sortCampaignsForGrid(campaigns), [campaigns]);

    const parsedSourceProductId =
        sourceProductId !== "" && Number.isFinite(Number(sourceProductId)) ? Number(sourceProductId) : null;

    const { data: sourceProductPreview, isFetching: isSourceProductPreviewLoading } =
        useIngredientReferenceFromProductPreview({
            productId: parsedSourceProductId,
            enabled: open && !isEditing && parsedSourceProductId !== null,
        });

    /**
     * Build a 2D scores map (scoreId × campaignId) so the grid can render an input
     * for every cell. Existing values are seeded from `editing.scores`, every other
     * cell defaults to `null` — sending `null` round-trips and preserves the row,
     * which matches `IngredientReference::setScores()` orphan-protection semantics.
     */
    const buildScoresMap = useMemo(
        () =>
            (editingRef?: IngredientReference | null): Record<string, Record<string, number | null>> => {
                const acc: Record<string, Record<string, number | null>> = {};
                for (const f of scoreFields) {
                    acc[String(f.scoreId)] = {};
                    for (const campaign of sortedCampaigns) {
                        acc[String(f.scoreId)][String(campaign.id)] = null;
                    }
                }

                if (editingRef?.scores) {
                    for (const s of editingRef.scores) {
                        const campaignId = campaignIdFromIri(s.campaign);
                        if (campaignId === null) continue;
                        if (!acc[String(s.scoreId)]) {
                            acc[String(s.scoreId)] = {};
                        }
                        acc[String(s.scoreId)][String(campaignId)] = s.value ?? null;
                    }
                }

                return acc;
            },
        [scoreFields, sortedCampaigns],
    );

    const {
        control,
        handleSubmit,
        formState: { errors, isValid },
        reset,
    } = useForm<FormData>({
        mode: "onChange",
        resolver: zodResolver(FormSchema),
        defaultValues: {
            id: undefined,
            name: "",
            code: "",
            description: "",
            cpcCode: "",
            defaultOrigin: "",
            organic: false,
            fresh: false,
            animal: false,
            scores: {},
            ingredientReferenceCompanies: [],
        },
    });

    useEffect(() => {
        if (!open) {
            appliedPreviewKeyRef.current = null;
            return;
        }

        setSourceProductEntityType("");
        setSourceProductId("");

        reset({
            id: editing?.id,
            name: editing?.name ?? "",
            code: editing?.code ?? "",
            description: editing?.description ?? "",
            cpcCode: editing?.cpcCode ?? "",
            defaultOrigin: editing?.defaultOrigin ?? "",
            organic: editing?.organic ?? false,
            fresh: editing?.fresh ?? false,
            animal: editing?.animal ?? false,
            scores: buildScoresMap(editing),
            // `?? []` covers brand-new references and edge cases where the backend
            // omits the field entirely (rolling deploy window before the migration).
            ingredientReferenceCompanies: editing?.ingredientReferenceOrganizations ?? [],
        });
    }, [open, editing, reset, buildScoresMap]);

    useEffect(() => {
        appliedPreviewKeyRef.current = null;
    }, [parsedSourceProductId]);

    useEffect(() => {
        if (!open || isEditing || !sourceProductPreview || parsedSourceProductId === null) return;
        if (isSourceProductPreviewLoading) return;
        if (sortedCampaigns.length === 0 || scoreFields.length === 0) return;

        const previewKey = `${parsedSourceProductId}:${sortedCampaigns.length}:${scoreFields.length}`;
        if (appliedPreviewKeyRef.current === previewKey) {
            return;
        }
        appliedPreviewKeyRef.current = previewKey;

        const scoresMap = buildScoresMap();
        for (const row of sourceProductPreview.scores) {
            const scoreKey = String(row.scoreId);
            const campaignKey = String(row.campaignId);
            if (!scoresMap[scoreKey]) {
                scoresMap[scoreKey] = {};
            }
            scoresMap[scoreKey][campaignKey] = row.value;
        }

        reset({
            name: sourceProductPreview.name,
            code: sourceProductPreview.code ?? "",
            description: "",
            cpcCode: "",
            defaultOrigin: "",
            organic: false,
            fresh: false,
            animal: false,
            scores: scoresMap,
            ingredientReferenceCompanies: [],
        });
    }, [
        open,
        isEditing,
        sourceProductPreview,
        parsedSourceProductId,
        isSourceProductPreviewLoading,
        sortedCampaigns.length,
        scoreFields.length,
        buildScoresMap,
        reset,
    ]);

    const onSubmitHandler: SubmitHandler<FormData> = async (formData) => {
        try {
            const scoresPayload: IngredientReferenceScoreWrite[] = [];
            for (const scoreField of scoreFields) {
                const perCampaign = formData.scores?.[String(scoreField.scoreId)] ?? {};
                for (const campaign of sortedCampaigns) {
                    const raw = perCampaign[String(campaign.id)];
                    scoresPayload.push({
                        scoreId: scoreField.scoreId,
                        value: typeof raw === "number" && Number.isFinite(raw) ? raw : null,
                        campaign: campaign["@id"] ?? buildCampaignIri(campaign.id),
                    });
                }
            }

            const associationsPayload: IngredientReferenceCompanyWrite[] | undefined =
                canManageAssociations ?
                    formData.ingredientReferenceCompanies.map((row) => ({
                        ...(row["@id"] ? { "@id": row["@id"] } : {}),
                        organization: row.organization["@id"],
                    }))
                :   undefined;

            if (!isEditing && parsedSourceProductId !== null) {
                await createMutation.mutateAsync({
                    payload: {
                        product: `/api/products/${parsedSourceProductId}`,
                        name: formData.name.trim(),
                        code: formData.code?.trim() || null,
                        description: formData.description?.trim() || null,
                        cpcCode: formData.cpcCode?.trim() || null,
                        defaultOrigin: formData.defaultOrigin?.trim() || null,
                        organic: formData.organic ?? null,
                        fresh: formData.fresh ?? null,
                        animal: formData.animal ?? null,
                        scores: scoresPayload,
                    },
                });
            } else {
                const payload: IngredientReferenceWrite = {
                    name: formData.name.trim(),
                    code: formData.code?.trim() || null,
                    description: formData.description?.trim() || null,
                    cpcCode: formData.cpcCode?.trim() || null,
                    defaultOrigin: formData.defaultOrigin?.trim() || null,
                    organic: formData.organic ?? null,
                    fresh: formData.fresh ?? null,
                    animal: formData.animal ?? null,
                    scores: scoresPayload,
                    ...(isEditing && formData.id !== undefined ? { id: formData.id } : {}),
                    ...(associationsPayload !== undefined ?
                        { ingredientReferenceOrganizations: associationsPayload }
                    :   {}),
                };

                await createMutation.mutateAsync({ ingredientReference: payload });
            }

            setSnackbarProps({
                message:
                    isEditing ?
                        t("ingredientReferences.updateSuccess", "Reference updated successfully")
                    :   t("ingredientReferences.createSuccess", "Reference created successfully"),
                type: "success",
            });

            onSuccess();
            onClose();
        } catch (error: any) {
            const errorData = error?.response?.data;
            const message =
                errorData?.["hydra:description"] ||
                errorData?.["hydra:title"] ||
                error?.message ||
                t("ingredientReferences.saveError", "Failed to save ingredient reference");

            setSnackbarProps({ message, type: "error" });
        }
    };

    const handleClose = () => {
        setSourceProductEntityType("");
        setSourceProductId("");
        reset();
        onClose();
    };

    const inputSx = {
        "& .MuiOutlinedInput-root": {
            borderRadius: "24px",
            minHeight: "40px",
            backgroundColor: "white",
            "& fieldset": { borderColor: "#E7E6E4" },
            "&:hover fieldset": { borderColor: "#D0D0D0" },
            "&.Mui-focused fieldset": { borderColor: "primary.main" },
        },
        "& .MuiInputBase-input": {
            fontSize: "0.875rem",
            padding: "0px 16px !important",
            color: "#393A36",
        },
    };

    const selectSx = {
        borderRadius: "24px",
        height: "40px",
        backgroundColor: "white",
        "& fieldset": { borderColor: "#E7E6E4" },
        "&:hover fieldset": { borderColor: "#D0D0D0" },
        "&.Mui-focused fieldset": { borderColor: "primary.main" },
        "& .MuiSelect-select": {
            fontSize: "0.875rem",
            padding: "0px 16px",
            color: "#393A36",
            display: "flex",
            alignItems: "center",
        },
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { borderRadius: "16px", overflow: "hidden", maxHeight: "90vh" },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "32px",
                    borderBottom: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <Box>
                    <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E", marginBottom: "8px" }}>
                        {isEditing ?
                            t("ingredientReferences.editTitle", "Edit ingredient reference")
                        :   t("ingredientReferences.createTitle", "Create ingredient reference")}
                    </Typography>
                    <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#50504D" }}>
                        {isEditing ?
                            t("ingredientReferences.editDescription", "Update the platform reference data.")
                        :   t(
                                "ingredientReferences.createDescription",
                                "Set up a new platform-defined ingredient reference.",
                            )
                        }
                    </Typography>
                </Box>
                <Box
                    sx={{
                        cursor: "pointer",
                        padding: "8px",
                        borderRadius: "50%",
                        "&:hover": { backgroundColor: "#F5F5F5" },
                    }}
                    onClick={handleClose}
                >
                    <CloseIcon />
                </Box>
            </Box>

            <Box sx={{ padding: "32px", maxHeight: "calc(90vh - 200px)", overflowY: "auto" }}>
                <form onSubmit={handleSubmit(onSubmitHandler)}>
                    <Stack spacing={3}>
                        {!isEditing && (
                            <Box>
                                <Typography
                                    variant="h5"
                                    sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "16px" }}
                                >
                                    {t("ingredientReferences.fromProductTitle", "Create from a product")}
                                </Typography>
                                <Typography
                                    variant="small-medium"
                                    sx={{ lineHeight: "1.6em", color: "#50504D", marginBottom: "16px" }}
                                >
                                    {t(
                                        "ingredientReferences.fromProductDescription",
                                        "Optionally pick a finished product or intermediate product to copy its name, code, and OLAP scores as fixed reference values.",
                                    )}
                                </Typography>
                                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                lineHeight: "1.6em",
                                                color: "#393A36",
                                                marginBottom: "8px",
                                            }}
                                        >
                                            {t("ingredientReferences.fromProductTypeLabel", "Source type")}:
                                        </Typography>
                                        <FormControl fullWidth>
                                            <Select
                                                value={sourceProductEntityType}
                                                onChange={(event) => {
                                                    setSourceProductEntityType(
                                                        event.target.value as SourceProductEntityType | "",
                                                    );
                                                    setSourceProductId("");
                                                }}
                                                displayEmpty
                                                renderValue={(selected) =>
                                                    selected ?
                                                        selected === "product" ?
                                                            t("product", "Product")
                                                        :   t("intermediate_product", "Intermediate product")
                                                    :   <span style={{ color: "#A3A2A0" }}>
                                                            {t(
                                                                "ingredientReferences.fromProductTypePlaceholder",
                                                                "Manual entry",
                                                            )}
                                                        </span>
                                                }
                                                sx={selectSx}
                                            >
                                                <MenuItem value="">
                                                    {t(
                                                        "ingredientReferences.fromProductTypePlaceholder",
                                                        "Manual entry",
                                                    )}
                                                </MenuItem>
                                                <MenuItem value="product">{t("product", "Product")}</MenuItem>
                                                <MenuItem value="intermediate_product">
                                                    {t("intermediate_product", "Intermediate product")}
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    {sourceProductEntityType !== "" && (
                                        <Box sx={{ flex: 2 }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    lineHeight: "1.6em",
                                                    color: "#393A36",
                                                    marginBottom: "8px",
                                                }}
                                            >
                                                {t("ingredientReferences.fromProductLabel", "Product")}:
                                            </Typography>
                                            <FormProductSelector
                                                key={sourceProductEntityType}
                                                value={sourceProductId}
                                                onChange={setSourceProductId}
                                                intermediateProducts={
                                                    sourceProductEntityType === "intermediate_product"
                                                }
                                                sx={inputSx}
                                            />
                                            {isSourceProductPreviewLoading && (
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                                                    <CircularProgress size={16} />
                                                    <Typography variant="small-medium" color="text.secondary">
                                                        {t(
                                                            "ingredientReferences.fromProductLoading",
                                                            "Copying scores from product…",
                                                        )}
                                                    </Typography>
                                                </Stack>
                                            )}
                                        </Box>
                                    )}
                                </Stack>
                            </Box>
                        )}

                        <Box>
                            <Typography
                                variant="h5"
                                sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "16px" }}
                            >
                                {t("ingredientReferences.basicInformation", "Basic information")}
                            </Typography>
                            <Stack spacing={2}>
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            lineHeight: "1.6em",
                                            color: "#393A36",
                                            marginBottom: "8px",
                                        }}
                                    >
                                        {t("ingredientReferences.nameLabel", "Name")}*:
                                    </Typography>
                                    <Controller
                                        name="name"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                value={field.value ?? ""}
                                                fullWidth
                                                error={!!errors.name}
                                                helperText={errors.name?.message}
                                                placeholder={t("ingredientReferences.namePlaceholder", "Enter name")}
                                                sx={inputSx}
                                            />
                                        )}
                                    />
                                </Box>
                                <Stack direction="row" spacing={2}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                lineHeight: "1.6em",
                                                color: "#393A36",
                                                marginBottom: "8px",
                                            }}
                                        >
                                            {t("ingredientReferences.codeLabel", "Code")}:
                                        </Typography>
                                        <Controller
                                            name="code"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    value={field.value ?? ""}
                                                    fullWidth
                                                    placeholder={t(
                                                        "ingredientReferences.codePlaceholder",
                                                        "Optional code",
                                                    )}
                                                    sx={inputSx}
                                                />
                                            )}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                lineHeight: "1.6em",
                                                color: "#393A36",
                                                marginBottom: "8px",
                                            }}
                                        >
                                            {t("ingredientReferences.cpcCodeLabel", "CPC code")}:
                                        </Typography>
                                        <Controller
                                            name="cpcCode"
                                            control={control}
                                            render={({ field }) => (
                                                <FormCpcSelector
                                                    value={field.value ?? null}
                                                    onChange={field.onChange}
                                                    sx={inputSx}
                                                />
                                            )}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                lineHeight: "1.6em",
                                                color: "#393A36",
                                                marginBottom: "8px",
                                            }}
                                        >
                                            {t("ingredientReferences.defaultOriginLabel", "Default origin")}:
                                        </Typography>
                                        <Controller
                                            name="defaultOrigin"
                                            control={control}
                                            render={({ field }) => (
                                                <FormGeographySelector
                                                    value={field.value ?? null}
                                                    onChange={field.onChange}
                                                    error={!!errors.defaultOrigin}
                                                    helperText={errors.defaultOrigin?.message}
                                                    sx={inputSx}
                                                />
                                            )}
                                        />
                                    </Box>
                                </Stack>
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            lineHeight: "1.6em",
                                            color: "#393A36",
                                            marginBottom: "8px",
                                        }}
                                    >
                                        {t("ingredientReferences.descriptionLabel", "Description")}:
                                    </Typography>
                                    <Controller
                                        name="description"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                value={field.value ?? ""}
                                                fullWidth
                                                multiline
                                                rows={3}
                                                placeholder={t(
                                                    "ingredientReferences.descriptionPlaceholder",
                                                    "Enter description",
                                                )}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "24px",
                                                        backgroundColor: "white",
                                                        "& fieldset": { borderColor: "#E7E6E4" },
                                                    },
                                                    "& .MuiInputBase-input": {
                                                        fontSize: "0.875rem",
                                                        padding: "16px !important",
                                                        color: "#393A36",
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                                <Stack direction="row" spacing={3}>
                                    <Controller
                                        name="organic"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={field.value ?? false}
                                                        onChange={(e) => field.onChange(e.target.checked)}
                                                    />
                                                }
                                                label={t("ingredientReferences.organic", "Organic")}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="fresh"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={field.value ?? false}
                                                        onChange={(e) => field.onChange(e.target.checked)}
                                                    />
                                                }
                                                label={t("ingredientReferences.fresh", "Fresh")}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="animal"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={field.value ?? false}
                                                        onChange={(e) => field.onChange(e.target.checked)}
                                                    />
                                                }
                                                label={t("ingredientReferences.animal", "Animal")}
                                            />
                                        )}
                                    />
                                </Stack>
                            </Stack>
                        </Box>

                        {canManageAssociations && (
                            <>
                                <Divider sx={{ borderColor: "#E7E6E4" }} />
                                <Box>
                                    <Typography
                                        variant="h5"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "16px" }}
                                    >
                                        {t(
                                            "ingredientReferences.associatedStructuresTitle",
                                            "Associated organisations",
                                        )}
                                    </Typography>
                                    <Controller
                                        name="ingredientReferenceCompanies"
                                        control={control}
                                        render={({ field }) => (
                                            <AssociatedStructuresField
                                                value={
                                                    // The form stores the same shape (`{ company: { @id, id, name } }`)
                                                    // as `IngredientFormAssociatedCompany`, but TS keeps them nominally
                                                    // distinct. Cast at the boundary instead of duplicating the
                                                    // autocomplete component for a cosmetic type difference.
                                                    field.value as unknown as IngredientFormAssociatedCompany[]
                                                }
                                                onChange={(value) =>
                                                    field.onChange(
                                                        value as unknown as FormData["ingredientReferenceCompanies"],
                                                    )
                                                }
                                                ownedBy={
                                                    (editing?.ownedBy ??
                                                        (selectedCompany ?
                                                            {
                                                                "@id":
                                                                    selectedCompany["@id"] ??
                                                                    `/api/organizations/${selectedCompany.id}`,
                                                                id: selectedCompany.id,
                                                                name: selectedCompany.name,
                                                            }
                                                        :   null)) as IngredientFormCompany | null
                                                }
                                            />
                                        )}
                                    />
                                </Box>
                            </>
                        )}

                        <Divider sx={{ borderColor: "#E7E6E4" }} />

                        <Box>
                            <Typography
                                variant="h5"
                                sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                            >
                                {t("ingredientReferences.scoresTitle", "Emission factors (scores)")}
                            </Typography>
                            <Typography
                                variant="small-medium"
                                sx={{ color: "#50504D", marginBottom: "16px", display: "block" }}
                            >
                                {t(
                                    "ingredientReferences.scoresHelp",
                                    "Leave a field empty to skip a score; only filled values are sent to the calculation engine.",
                                )}
                            </Typography>

                            {isLoadingFields || isLoadingCampaigns ?
                                <Box sx={{ display: "flex", justifyContent: "center", padding: "16px" }}>
                                    <CircularProgress size={24} />
                                </Box>
                            : scoreFields.length === 0 ?
                                <Typography sx={{ color: "#A3A2A0" }}>
                                    {t(
                                        "ingredientReferences.noScores",
                                        "No score is currently configured at the platform level for ingredients.",
                                    )}
                                </Typography>
                            : sortedCampaigns.length === 0 ?
                                <Typography sx={{ color: "#A3A2A0" }}>
                                    {t(
                                        "ingredientReferences.noCampaigns",
                                        "No campaign is configured. Create a campaign before assigning scores.",
                                    )}
                                </Typography>
                            :   <TableContainer
                                    sx={{
                                        border: "1px solid #E7E6E4",
                                        borderRadius: "12px",
                                        backgroundColor: "white",
                                    }}
                                    aria-label={t(
                                        "ingredientReferences.scoresGridAriaLabel",
                                        "Scores per campaign grid",
                                    )}
                                >
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: "#393A36",
                                                        backgroundColor: "#FAFAFA",
                                                        position: "sticky",
                                                        left: 0,
                                                        zIndex: 2,
                                                    }}
                                                >
                                                    {t("ingredientReferences.scoreColumnLabel", "Score")}
                                                </TableCell>
                                                {sortedCampaigns.map((campaign) => (
                                                    <TableCell
                                                        key={campaign.id}
                                                        align="center"
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: "#393A36",
                                                            backgroundColor: "#FAFAFA",
                                                            minWidth: "160px",
                                                        }}
                                                    >
                                                        <Stack alignItems="center" spacing={0}>
                                                            <Typography variant="small-medium" sx={{ fontWeight: 700 }}>
                                                                {campaign.label}
                                                            </Typography>
                                                            {currentCampaign?.id === campaign.id && (
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{ color: "primary.main" }}
                                                                >
                                                                    {t(
                                                                        "ingredientReferences.activeCampaignTag",
                                                                        "active",
                                                                    )}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {scoreFields.map((field) => (
                                                <TableRow key={field.scoreId} hover>
                                                    <TableCell
                                                        sx={{
                                                            color: "#393A36",
                                                            fontWeight: 600,
                                                            backgroundColor: "white",
                                                            position: "sticky",
                                                            left: 0,
                                                            zIndex: 1,
                                                        }}
                                                    >
                                                        {field.label}
                                                        {field.unit ? ` (${field.unit})` : ""}
                                                    </TableCell>
                                                    {sortedCampaigns.map((campaign) => (
                                                        <TableCell
                                                            key={`${field.scoreId}-${campaign.id}`}
                                                            align="center"
                                                            sx={{ padding: "8px" }}
                                                        >
                                                            <Controller
                                                                name={`scores.${field.scoreId}.${campaign.id}` as const}
                                                                control={control}
                                                                render={({ field: f }) => (
                                                                    <TextField
                                                                        fullWidth
                                                                        type="number"
                                                                        inputProps={{
                                                                            step: "any",
                                                                            "aria-label": `${field.label} – ${campaign.label}`,
                                                                        }}
                                                                        value={f.value ?? ""}
                                                                        onChange={(e) => {
                                                                            const v = e.target.value;
                                                                            if (v === "") {
                                                                                f.onChange(null);
                                                                                return;
                                                                            }
                                                                            const n = parseFloat(v);
                                                                            f.onChange(isNaN(n) ? null : n);
                                                                        }}
                                                                        onBlur={f.onBlur}
                                                                        sx={inputSx}
                                                                    />
                                                                )}
                                                            />
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            }
                        </Box>
                    </Stack>
                </form>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    padding: "0px 24px 0px 32px",
                    height: "72px",
                    backgroundColor: "white",
                    borderTop: "1px solid #E7E6E4",
                }}
            >
                <Button
                    onClick={handleSubmit(onSubmitHandler)}
                    disabled={!isValid || isProcessing}
                    sx={{
                        width: "150px",
                        height: "40px",
                        borderRadius: "44px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        backgroundColor: "primary.main",
                        color: "white",
                        opacity: isValid ? 1 : 0.36,
                        "&:hover": { backgroundColor: "primary.dark" },
                        "&:disabled": { backgroundColor: "primary.main", color: "white" },
                    }}
                >
                    {isProcessing ?
                        <CircularProgress size={20} color="inherit" />
                    : isEditing ?
                        t("common.update", "Update")
                    :   t("common.create", "Create")}
                </Button>
            </Box>
        </Dialog>
    );
};
